import React, { useEffect, useMemo, useState } from "react";
import { toast } from "../ui/Toast";

// Manager salary-review allocation GRID (Session A). One row per report:
// employee | perf score (advisory) | current salary | increase % (inline) |
// new salary (computed) | notes | status. SAVE-ON-BLUR: leaving the % or notes
// field PATCHes /allocations/{id}; the response gives {status:"submitted",
// proposed_new_salary}. A 422 (team total over the cycle budget) is surfaced
// (toast + red row/total), never swallowed. Editing is only enabled on an OPEN
// cycle. Allocation status: pending|submitted|approved|applied.
// NOTE: this grid never calls /apply — that is deferred to Session B.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BASE = `${API_BASE}/api/payroll/salary-review`;
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const cellInput = "w-24 bg-black border border-[#00ff90]/20 rounded-lg px-2 py-1 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const notesInput = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-2 py-1 text-xs text-white outline-none focus:border-[#00ff90]/50 transition-colors";

const STATUS_STYLE = {
  pending:   "text-white/50 bg-white/5 border-white/15",
  submitted: "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  approved:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  applied:   "text-blue-400 bg-blue-500/10 border-blue-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { pending: "Pending", submitted: "Submitted", approved: "Approved", applied: "Applied" };

const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v));
const money = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const statusBadge = (s) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[s] || STATUS_FALLBACK}`}>{STATUS_LABEL[s] || s || "—"}</span>
);

export default function SalaryReviewGrid({ cycle }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits]     = useState({});   // { [id]: { pct, notes } }
  const [dirty, setDirty]     = useState({});   // { [id]: true }
  const [rowErr, setRowErr]   = useState({});   // { [id]: msg }
  const [savingId, setSavingId] = useState(null);

  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const editable = cycle?.status === "open";

  const load = async () => {
    if (!cycle?.id) { setRows([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/${cycle.id}/my-team`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setEdits(Object.fromEntries(list.map(r => [r.id, { pct: r.proposed_increase_pct ?? "", notes: r.manager_notes ?? "" }])));
      setDirty({}); setRowErr({});
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [cycle?.id]); // eslint-disable-line

  const setEdit = (id, field, v) => {
    setEdits(e => ({ ...e, [id]: { ...e[id], [field]: v } }));
    setDirty(d => ({ ...d, [id]: true }));
  };

  const saveRow = async (row) => {
    if (!dirty[row.id] || !editable) return;
    const e = edits[row.id] || {};
    setSavingId(row.id);
    try {
      const body = {
        proposed_increase_pct: e.pct === "" ? null : Number(e.pct),
        manager_notes: e.notes || null,
      };
      const res = await fetch(`${BASE}/allocations/${row.id}`, {
        method: "PATCH", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 422) {
        // Budget guard — surface it; keep the edited value + dirty so it can be fixed.
        const msg = data.detail || "Team increase exceeds the cycle budget";
        setRowErr(r => ({ ...r, [row.id]: msg }));
        toast.error(msg);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRows(rs => rs.map(r => r.id === row.id
        ? { ...r, proposed_new_salary: data.proposed_new_salary, status: data.status,
            proposed_increase_pct: e.pct === "" ? null : Number(e.pct), manager_notes: e.notes }
        : r));
      setRowErr(r => { const n = { ...r }; delete n[row.id]; return n; });
      setDirty(d => { const n = { ...d }; delete n[row.id]; return n; });
      toast.success("Saved");
    } catch { toast.error("Failed to save allocation"); }
    finally { setSavingId(null); }
  };

  // Live projected team total from the current edit state (advisory indicator).
  const team = useMemo(() => {
    let inc = 0, cur = 0;
    for (const r of rows) {
      const c = num(r.current_salary);
      inc += c * num(edits[r.id]?.pct) / 100;
      cur += c;
    }
    const pct = cur ? (inc / cur) * 100 : 0;
    const budget = cycle?.total_budget_pct != null ? Number(cycle.total_budget_pct) : null;
    return { pct, budget, over: budget != null && pct > budget };
  }, [rows, edits, cycle]);

  if (!cycle) {
    return <div className={`${card} p-6 text-white/25 text-sm italic text-center`}>Select a cycle above to view your team's allocations.</div>;
  }
  if (!loading && cycle.status !== "open" && rows.length === 0) {
    return <div className={`${card} p-6 text-white/25 text-sm italic text-center`}>This cycle isn't open — no allocations to edit.</div>;
  }

  return (
    <div className={`${card} overflow-hidden`}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">My Team — {cycle.name}</span>
        {team.budget != null && (
          <span className={`text-xs px-3 py-1 rounded-full border ${team.over ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-white/10 bg-white/5 text-white/50"}`}>
            Team total {team.pct.toFixed(1)}% / budget {team.budget}%{team.over ? " — over budget" : ""}
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-5 space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-9 rounded-lg bg-white/5 animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <p className="px-5 py-8 text-white/20 text-sm italic text-center">No allocations for your team in this cycle.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#00ff90]/40 uppercase tracking-wider border-b border-white/5">
                <th className="px-4 py-2 font-medium">Employee</th>
                <th className="px-4 py-2 font-medium">Score</th>
                <th className="px-4 py-2 font-medium text-right">Current</th>
                <th className="px-4 py-2 font-medium">Increase %</th>
                <th className="px-4 py-2 font-medium text-right">New Salary</th>
                <th className="px-4 py-2 font-medium">Notes</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const e = edits[r.id] || {};
                const liveNew = num(r.current_salary) * (1 + num(e.pct) / 100);
                const err = rowErr[r.id];
                return (
                  <tr key={r.id} className={`border-b border-white/5 ${err ? "bg-red-500/5" : ""}`}>
                    <td className="px-4 py-2 text-white/80">{r.employee_name || r.user_id.slice(0, 8)}</td>
                    <td className="px-4 py-2">
                      {r.perf_score != null || r.perf_rating ? (
                        <span className="text-xs text-white/50">
                          {r.perf_rating || "—"}{r.perf_score != null ? ` (${r.perf_score})` : ""}
                        </span>
                      ) : <span className="text-xs text-white/20">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right text-white/60">{money(r.current_salary)}</td>
                    <td className="px-4 py-2">
                      <input type="number" className={`${cellInput} ${err ? "border-red-500/50" : ""}`}
                        value={e.pct} disabled={!editable || savingId === r.id}
                        onChange={ev => setEdit(r.id, "pct", ev.target.value)}
                        onBlur={() => saveRow(r)} />
                    </td>
                    <td className="px-4 py-2 text-right text-white/70">{e.pct === "" ? money(r.proposed_new_salary) : money(liveNew)}</td>
                    <td className="px-4 py-2 min-w-[160px]">
                      <input className={notesInput} value={e.notes} disabled={!editable || savingId === r.id}
                        placeholder="notes…"
                        onChange={ev => setEdit(r.id, "notes", ev.target.value)}
                        onBlur={() => saveRow(r)} />
                    </td>
                    <td className="px-4 py-2">
                      {statusBadge(r.status)}
                      {err && <p className="text-xs text-red-400 mt-0.5 max-w-[180px]">{err}</p>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!editable && rows.length > 0 && (
        <p className="px-5 py-2 text-xs text-white/25 border-t border-white/5">This cycle is {cycle.status} — allocations are read-only.</p>
      )}
    </div>
  );
}
